// shell/components/segmented-control.ts
// A one-of-N view-mode switch: flush buttons, one pressed.
//
// This is the "two hand-built tab bars" shape (schema-manager's detail and
// editor, cam's table|grid, the agent file view's Current | At-read-time) —
// a *view* choice on the pane's own subject, so it belongs in the title row
// next to the verbs rather than in a filter bar. `renderPaneActions` builds
// one for every `{ kind: 'segmented' }` descriptor.
//
// Related but different: `createFilterBar` (utils/pane-ui.ts) is the same
// visual idea persisted into the pane's context for a LIST's scope; this one
// owns no storage and re-plans with the pane.

import { createElement } from '../utils/dom';

export interface SegmentedControlOption {
    value: string;
    label: string;
    /** Tooltip for this option. */
    title?: string;
    /**
     * Draw a small dot on this option — an "there is something here" hint
     * (the agent file view's drift dot, an unread count's stand-in).
     */
    indicator?: boolean;
}

export interface SegmentedControlConfig {
    /** Currently selected value. */
    value: string;
    options: readonly SegmentedControlOption[];
    disabled?: boolean;
    /** Tooltip on the group. When disabled, this should say why. */
    title?: string;
    /** `'small'` for a title row; `'medium'` (the default) elsewhere. */
    size?: 'small' | 'medium';
    /** Additional CSS class on the group. */
    className?: string;
    /** Fires when the selection MOVES — never for a click on the active option. */
    onChange: (value: string) => void;
}

export interface SegmentedControl {
    /** The group element to place in the row. */
    element: HTMLElement;
    getValue: () => string;
    /** Move the selection without reporting a change. */
    setValue: (value: string) => void;
}

/**
 * Build a segmented control.
 *
 * @example
 * ```typescript
 * const view = createSegmentedControl({
 *     value: mode,
 *     size: 'small',
 *     options: [
 *         { value: 'current', label: 'Current' },
 *         { value: 'at-read', label: 'At read time', indicator: drifted },
 *     ],
 *     onChange: (next) => { mode = next; render(); },
 * });
 * titleRow.appendChild(view.element);
 * ```
 */
export function createSegmentedControl(config: SegmentedControlConfig): SegmentedControl {
    const { options, disabled = false, title, size = 'medium', className, onChange } = config;

    let current = config.value;

    const classes = ['cs-segmented'];
    if (size === 'small') classes.push('cs-segmented--small');
    if (className) classes.push(className);

    const group = createElement('div', {
        className: classes.join(' '),
        attributes: { role: 'radiogroup', ...(title ? { title } : {}) },
    });

    const buttons = new Map<string, HTMLButtonElement>();

    const paint = (): void => {
        for (const [value, btn] of buttons) {
            const active = value === current;
            btn.classList.toggle('cs-active', active);
            btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        }
    };

    for (const option of options) {
        const btn = createElement('button', {
            className: 'cs-segmented-option',
            attributes: {
                type: 'button',
                'aria-pressed': 'false',
                ...(option.title ? { title: option.title } : {}),
            },
        });
        btn.dataset.value = option.value;
        btn.appendChild(createElement('span', {
            className: 'cs-segmented-label',
            textContent: option.label,
        }));
        if (option.indicator) {
            btn.appendChild(createElement('span', { className: 'cs-segmented-indicator' }));
        }
        if (disabled) btn.disabled = true;
        btn.addEventListener('click', () => {
            if (btn.disabled || option.value === current) return;
            current = option.value;
            paint();
            onChange(current);
        });
        buttons.set(option.value, btn);
        group.appendChild(btn);
    }

    paint();

    return {
        element: group,
        getValue: () => current,
        setValue: (value: string) => {
            if (value === current || !buttons.has(value)) return;
            current = value;
            paint();
        },
    };
}
